/**
 * @fileoverview Provides utility functions for generating and opening Cursor deeplinks to install MCP servers, using `generateCursorDeeplink`, `generateRefCursorDeeplink`, `openCursorDeeplink`, and `installRefInCursor`. It utilizes the `McpServerConfig` interface and `McpMode` enum to define server configurations.
 */

export interface McpServerConfig {
  command?: string
  env?: Record<string, string>
  type?: 'stdio' | 'http'
  url?: string
}

export enum McpMode {
  STDIO = 'stdio',
  HTTP = 'http',
}

/**
 * Generates a Cursor deeplink for installing an MCP server
 * @param serverName - The name of the MCP server
 * @param serverConfig - The server configuration object
 * @returns The Cursor deeplink URL
 */
export const generateCursorDeeplink = (
  serverName: string,
  serverConfig: McpServerConfig,
): string => {
  // Base64 encode the server configuration
  const base64Config = btoa(JSON.stringify(serverConfig))

  // Generate the cursor deeplink
  return `cursor://anysphere.cursor-deeplink/mcp/install?name=${encodeURIComponent(serverName)}&config=${encodeURIComponent(base64Config)}`
}

/**
 * Generates a Cursor deeplink specifically for the Ref MCP server
 * @param apiKey - The Ref API key
 * @param mode - The MCP mode (STDIO for legacy, HTTP for recommended)
 * @param mcpDomain - The MCP domain (defaults to environment variable)
 * @returns The Cursor deeplink URL
 */
export const generateRefCursorDeeplink = (
  apiKey: string,
  mode: McpMode = McpMode.HTTP,
  mcpDomain?: string,
): string => {
  const serverName = 'Ref'
  const domain = mcpDomain || import.meta.env.VITE_MCP_DOMAIN

  let serverConfig: McpServerConfig
  if (mode === McpMode.STDIO) {
    serverConfig = {
      type: 'stdio',
      command: 'npx ref-tools-mcp@latest',
      env: {
        REF_API_KEY: apiKey,
      },
    }
  } else {
    // Always use query param for HTTP transport as per review comment
    serverConfig = {
      type: 'http',
      url: `${domain}/mcp?apiKey=${apiKey}`,
    }
  }

  return generateCursorDeeplink(serverName, serverConfig)
}

/**
 * Opens a Cursor deeplink in the current window
 * @param deeplink - The Cursor deeplink URL to open
 */
export const openCursorDeeplink = (deeplink: string): void => {
  window.open(deeplink, '_self')
}

/**
 * Installs the Ref MCP server in Cursor
 * @param apiKey - The Ref API key
 * @param mode - The MCP mode (STDIO for legacy, HTTP for recommended)
 * @param mcpDomain - The MCP domain (defaults to environment variable)
 */
export const installRefInCursor = (apiKey: string, mode: McpMode, mcpDomain?: string): void => {
  const deeplink = generateRefCursorDeeplink(apiKey, mode, mcpDomain)
  openCursorDeeplink(deeplink)
}

/**
 * Generates a 1-click install URL for the Ref MCP server with API key as query parameter
 * @param apiKey - The Ref API key
 * @param mode - The MCP mode (STDIO for legacy, HTTP for recommended)
 * @param mcpDomain - The MCP domain (defaults to environment variable)
 * @returns The 1-click install URL
 */
export const generate1ClickInstallUrl = (
  apiKey: string,
  mode: McpMode = McpMode.HTTP,
  mcpDomain?: string,
): string => {
  return generateRefCursorDeeplink(apiKey, mode, mcpDomain)
}
